import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { RoomStatusUpdate, VoiceGroupStatusUpdate } from './useVoiceService';
import { useAuth } from '../contexts/AuthContext';

interface UseVoiceStatusStreamProps {
  roomIds?: string[];
  groupIds?: string[];
  includeInitialState?: boolean;
  enabled?: boolean;
}

export const useVoiceStatusStream = ({
  roomIds = [],
  groupIds = [],
  includeInitialState = true,
  enabled = true,
}: UseVoiceStatusStreamProps) => {
  const [roomStatusUpdates, setRoomStatusUpdates] = useState<RoomStatusUpdate[]>([]);
  const [groupStatusUpdates, setGroupStatusUpdates] = useState<VoiceGroupStatusUpdate[]>([]);
  const [initialRoomsData, setInitialRoomsData] = useState<any[]>([]);
  const [initialGroupsData, setInitialGroupsData] = useState<any[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialData, setHasInitialData] = useState(false);
  
  const roomStreamRef = useRef<WebSocket | null>(null);
  const groupStreamRef = useRef<WebSocket | null>(null);
  const { accessToken, user } = useAuth();
  const toast = useToast();

  const connectToRoomStream = useCallback(() => {
    if (!enabled || roomIds.length === 0 || !accessToken || !user?.id) return;

    try {
      const params = new URLSearchParams({
        include_initial_state: includeInitialState.toString(),
        access_token: accessToken,
        user_id: user.id,
      });

      // WebSocket connection
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let wsUrl: string;
      
      if (roomIds.length === 1) {
        // Single room stream - use specific room endpoint
        wsUrl = `${protocol}//${window.location.host}/ws/v1/voice/rooms/${roomIds[0]}/stream?${params}`;
      } else {
        // Multiple rooms stream - use rooms stream endpoint
        params.append('room_ids', roomIds.join(','));
        wsUrl = `${protocol}//${window.location.host}/ws/v1/voice/rooms/stream?${params}`;
      }
      
      console.log('Connecting to room WebSocket:', wsUrl);
      console.log('WebSocket params:', params.toString());
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Check if this is initial state data (ListRoomsResponse or GetRoomResponse)
          if (data.rooms && Array.isArray(data.rooms)) {
            // Initial state: ListRoomsResponse
            setInitialRoomsData(data.rooms);
            setHasInitialData(true);
          } else if (data.room && data.room.id) {
            // Initial state: GetRoomResponse
            setInitialRoomsData([data.room]);
            setHasInitialData(true);
          } else {
            // Real-time update: RoomStatusUpdate
            const update: RoomStatusUpdate = data;
            setRoomStatusUpdates(prev => [...prev.slice(-99), update]); // Keep last 100 updates
          }
        } catch (err) {
          console.error('Failed to parse room status update:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('Room status stream error:', event);
        setError('Connection to room status stream failed');
        setIsConnected(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (roomStreamRef.current === ws) {
            connectToRoomStream();
          }
        }, 5000);
      };

      roomStreamRef.current = ws;
    } catch (err) {
      console.error('Failed to establish room status stream:', err);
      setError('Failed to establish room status stream');
    }
  }, [roomIds, includeInitialState, enabled, accessToken, user?.id]);

  const connectToGroupStream = useCallback(() => {
    console.log('connectToGroupStream called:', { 
      enabled, 
      groupIds, 
      roomIds, 
      hasToken: !!accessToken, 
      hasUserId: !!user?.id,
      accessToken: accessToken ? accessToken.substring(0, 20) + '...' : 'null',
      userId: user?.id
    });
    
    if (!enabled || (groupIds.length === 0 && roomIds.length === 0) || !accessToken || !user?.id) {
      console.log('connectToGroupStream skipped due to conditions:', {
        enabled,
        hasGroupIds: groupIds.length > 0,
        hasRoomIds: roomIds.length > 0,
        hasToken: !!accessToken,
        hasUserId: !!user?.id,
        groupIds,
        roomIds
      });
      return;
    }

    try {
      const params = new URLSearchParams({
        include_initial_state: includeInitialState.toString(),
        access_token: accessToken,
        user_id: user.id,
      });

      if (roomIds.length > 0) {
        params.append('room_id', roomIds[0]); // For now, support one room at a time
      }

      // WebSocket connection
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let wsUrl: string;
      
      if (groupIds.length === 1) {
        // Single group stream - use specific group endpoint
        wsUrl = `${protocol}//${window.location.host}/ws/v1/voice/groups/${groupIds[0]}/stream?${params}`;
      } else if (groupIds.length > 1) {
        // Multiple groups stream - use groups stream endpoint
        params.append('group_ids', groupIds.join(','));
        wsUrl = `${protocol}//${window.location.host}/ws/v1/voice/groups/stream?${params}`;
      } else {
        // No specific groups, use general groups stream (with room_id if provided)
        wsUrl = `${protocol}//${window.location.host}/ws/v1/voice/groups/stream?${params}`;
      }
      
      console.log('Connecting to group WebSocket:', wsUrl);
      console.log('WebSocket params:', params.toString());
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('Group WebSocket connected successfully');
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Check if this is initial state data (ListVoiceGroupsResponse or GetVoiceGroupResponse)
          if (data.voice_groups && Array.isArray(data.voice_groups)) {
            // Initial state: ListVoiceGroupsResponse
            setInitialGroupsData(data.voice_groups);
            setHasInitialData(true);
          } else if (data.voice_group && data.voice_group.id) {
            // Initial state: GetVoiceGroupResponse
            setInitialGroupsData([data.voice_group]);
            setHasInitialData(true);
          } else {
            // Real-time update: VoiceGroupStatusUpdate
            const update: VoiceGroupStatusUpdate = data;
            setGroupStatusUpdates(prev => [...prev.slice(-99), update]); // Keep last 100 updates
          }
        } catch (err) {
          console.error('Failed to parse group status update:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('Group status stream error:', event);
        setError('Connection to group status stream failed');
        setIsConnected(false);
      };

      ws.onclose = (event) => {
        console.log('Group WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (groupStreamRef.current === ws) {
            console.log('Attempting to reconnect group WebSocket...');
            connectToGroupStream();
          }
        }, 5000);
      };

      groupStreamRef.current = ws;
    } catch (err) {
      console.error('Failed to establish group status stream:', err);
      setError('Failed to establish group status stream');
    }
  }, [groupIds, roomIds, includeInitialState, enabled, accessToken, user?.id]);

  useEffect(() => {
    console.log('useVoiceStatusStream effect triggered:', { 
      enabled, 
      groupIds, 
      roomIds,
      hasToken: !!accessToken,
      hasUser: !!user
    });
    
    if (enabled) {
      if (roomIds.length > 0) {
        console.log('Starting room stream for rooms:', roomIds);
        connectToRoomStream();
      }
      if (groupIds.length > 0) {
        console.log('Starting group stream for groups:', groupIds);
        connectToGroupStream();
      }
    }

    return () => {
      console.log('Cleaning up WebSocket connections');
      if (roomStreamRef.current) {
        roomStreamRef.current.close();
        roomStreamRef.current = null;
      }
      if (groupStreamRef.current) {
        groupStreamRef.current.close();
        groupStreamRef.current = null;
      }
      setIsConnected(false);
    };
  }, [connectToRoomStream, connectToGroupStream, enabled]);

  const clearUpdates = useCallback(() => {
    setRoomStatusUpdates([]);
    setGroupStatusUpdates([]);
  }, []);

  const clearAll = useCallback(() => {
    setRoomStatusUpdates([]);
    setGroupStatusUpdates([]);
    setInitialRoomsData([]);
    setInitialGroupsData([]);
    setHasInitialData(false);
  }, []);

  const getLatestRoomStatus = useCallback((roomId: string) => {
    return roomStatusUpdates
      .filter(update => update.room_id === roomId)
      .slice(-1)[0];
  }, [roomStatusUpdates]);

  const getLatestGroupStatus = useCallback((groupId: string) => {
    return groupStatusUpdates
      .filter(update => update.group_id === groupId)
      .slice(-1)[0];
  }, [groupStatusUpdates]);

  // Show connection status in toast if there are errors
  useEffect(() => {
    if (error) {
      toast({
        title: 'Connection Error',
        description: error,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  return {
    roomStatusUpdates,
    groupStatusUpdates,
    initialRoomsData,
    initialGroupsData,
    hasInitialData,
    isConnected,
    error,
    clearUpdates,
    clearAll,
    getLatestRoomStatus,
    getLatestGroupStatus,
  };
};